import { useCallback, useRef } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import { useSelector } from 'react-redux'
import { type Arguments, type KeyedMutator, useSWRConfig } from 'swr'

import {
  standardRecipeTileFetcher,
  type MenuURN,
  type MenuResponse,
  type MenuSearchResponse,
  type RecipeURN,
  type SearchStandardRecipeItem,
  type StandardRecipeItem,
} from '@library/api-menu-experience'
import type { RecipeUUID } from '@library/menu-service'

import { getBasketDate, getBasketMenuUuid, getNumPortions } from 'selectors/basket'

type UseMenuExperienceRecipeTileRefreshArgs = {
  menuId?: string
  menuUrn?: MenuURN
  mutateMenu: KeyedMutator<MenuResponse>
}

const replaceRecipeTiles = (
  menu: MenuResponse,
  updatedRecipe: StandardRecipeItem,
): MenuResponse => ({
  ...menu,
  content: menu.content.map((contentBlock) => {
    if (contentBlock.type === 'recipe-grid') {
      return {
        ...contentBlock,
        items: contentBlock.items.map((recipe) =>
          recipe.id === updatedRecipe.id ? updatedRecipe : recipe,
        ),
      }
    }

    if (contentBlock.type === 'campaign-banner') {
      return {
        ...contentBlock,
        items: contentBlock.items.map((item) =>
          item.type === 'standard-recipe' && item.id === updatedRecipe.id ? updatedRecipe : item,
        ),
      }
    }

    if (contentBlock.type === 'compact-campaign-banner') {
      return {
        ...contentBlock,
        items: contentBlock.items.map((item) =>
          item.id === updatedRecipe.id ? updatedRecipe : item,
        ),
      }
    }

    return contentBlock
  }),
})

const mergeSearchRecipeTile = (
  recipe: SearchStandardRecipeItem,
  updatedRecipe: StandardRecipeItem,
): SearchStandardRecipeItem => {
  const nextRecipe = recipe.id === updatedRecipe.id ? { ...recipe, ...updatedRecipe } : recipe
  const nextBase = recipe.base ? mergeSearchRecipeTile(recipe.base, updatedRecipe) : undefined

  return nextBase && nextBase !== recipe.base ? { ...nextRecipe, base: nextBase } : nextRecipe
}

const replaceSearchRecipeTile = (
  response: MenuSearchResponse,
  updatedRecipe: StandardRecipeItem,
): MenuSearchResponse =>
  response.type === 'search-landing'
    ? {
        ...response,
        content: response.content.map((contentBlock) =>
          contentBlock.type === 'suggested-recipe'
            ? { ...contentBlock, item: mergeSearchRecipeTile(contentBlock.item, updatedRecipe) }
            : contentBlock,
        ),
      }
    : {
        ...response,
        content: response.content.map((contentBlock) =>
          contentBlock.type === 'search-recipe-results'
            ? {
                ...contentBlock,
                items: contentBlock.items.map((recipe) =>
                  mergeSearchRecipeTile(recipe, updatedRecipe),
                ),
              }
            : contentBlock,
        ),
      }

type MenuExperienceSearchCache = {
  response: MenuSearchResponse
}

export const useMenuExperienceRecipeTileRefresh = ({
  menuId,
  menuUrn,
  mutateMenu,
}: UseMenuExperienceRecipeTileRefreshArgs) => {
  const { mutate: mutateCache } = useSWRConfig()
  const deliveryDate = useSelector(getBasketDate)
  const basketMenuUuid = useSelector(getBasketMenuUuid)
  const basketNumPortions = useSelector(getNumPortions)
  const latestRequestByRecipeId = useRef<Partial<Record<RecipeUUID, symbol>>>({})

  const refreshRecipeTile = useCallback(
    async (recipeId: RecipeUUID, numPortions: number, recipeUrn?: RecipeURN) => {
      if (!menuId) {
        return
      }

      const requestId = Symbol('menu-experience-recipe-tile-refresh')
      latestRequestByRecipeId.current[recipeId] = requestId

      const logRefreshFailure = () => {
        datadogRum.addAction('menu_experience_recipe_tile_refresh_failed', {
          menu_id: menuId,
          recipe_id: recipeId,
          num_portions: numPortions,
        })
      }

      const updatedRecipe = await standardRecipeTileFetcher({
        recipeId,
        recipeUrn,
        menuId,
        menuUrn,
        numPortions,
        deliveryDate,
      }).catch(() => {
        logRefreshFailure()

        return undefined
      })

      if (latestRequestByRecipeId.current[recipeId] !== requestId) {
        return
      }

      if (!updatedRecipe) {
        return
      }

      await Promise.all([
        mutateMenu(
          (menu: MenuResponse | undefined) =>
            menu ? replaceRecipeTiles(menu, updatedRecipe) : menu,
          { revalidate: false },
        ),
        mutateCache(
          (key?: Arguments) =>
            typeof key === 'object' &&
            key !== null &&
            'key' in key &&
            key.key === 'menu-experience-search' &&
            'input' in key &&
            typeof key.input === 'object' &&
            key.input !== null &&
            'menuId' in key.input &&
            key.input.menuId === basketMenuUuid &&
            'deliveryDate' in key.input &&
            key.input.deliveryDate === deliveryDate &&
            'numPortions' in key.input &&
            key.input.numPortions === basketNumPortions,
          (cached: MenuExperienceSearchCache | undefined) =>
            cached?.response
              ? {
                  ...cached,
                  response: replaceSearchRecipeTile(cached.response, updatedRecipe),
                }
              : cached,
          {
            revalidate: (data: MenuExperienceSearchCache | undefined): boolean =>
              data === undefined,
          },
        ),
      ])
    },
    [basketMenuUuid, basketNumPortions, deliveryDate, menuId, menuUrn, mutateCache, mutateMenu],
  )

  return { refreshRecipeTile }
}
